import React from "react";
import { BtnUi } from "~/components";
import styles from "./Hero.module.css";
import classNames from "classnames";
import ArrowDownIcon from "../../assets/offers/arrow-down.svg";

class HeroSlideshow extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentSlide: 0,
      isHovered: false,
      isTransitioning: false,
    };
    this.interval = null;
  }

  componentDidMount() {
    this.startAutoPlay();
  }

  componentWillUnmount() {
    this.stopAutoPlay();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.isHovered !== this.state.isHovered) {
      if (this.state.isHovered && this.props.pauseOnHover) {
        this.stopAutoPlay();
      } else if (!this.state.isHovered) {
        this.startAutoPlay();
      }
    }
  }

  startAutoPlay = () => {
    const { slides, autoPlayInterval } = this.props;
    if (!slides.length || slides.length <= 1) return;

    this.stopAutoPlay();
    this.interval = setInterval(() => {
      this.transitionToNextSlide();
    }, autoPlayInterval);
  };

  transitionToNextSlide = () => {
    const { slides } = this.props;
    this.setState({ isTransitioning: true });

    // Short delay to allow fade out animation
    setTimeout(() => {
      this.setState((prevState) => ({
        currentSlide: (prevState.currentSlide + 1) % slides.length,
        isTransitioning: false,
      }));
    }, 300); // Half of the CSS transition duration
  };

  transitionToSlide = (index) => {
    if (index === this.state.currentSlide) return;

    this.setState({ isTransitioning: true });

    setTimeout(() => {
      this.setState({
        currentSlide: index,
        isTransitioning: false,
      });
    }, 300);
  };

  stopAutoPlay = () => {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  };

  goToSlide = (index) => {
    this.transitionToSlide(index);
  };

  nextSlide = () => {
    const { slides } = this.props;
    const nextIndex = (this.state.currentSlide + 1) % slides.length;
    this.transitionToSlide(nextIndex);
  };

  prevSlide = () => {
    const { slides } = this.props;
    const prevIndex =
      (this.state.currentSlide - 1 + slides.length) % slides.length;
    this.transitionToSlide(prevIndex);
  };

  handleMouseEnter = () => {
    if (this.props.pauseOnHover) {
      this.setState({ isHovered: true });
    }
  };

  handleHeroClick = (e) => {
    // Don't change slide if clicking a button or link
    if (e.target.closest("button") || e.target.closest("a")) {
      return;
    }
    this.nextSlide();
  };

  handleMouseLeave = () => {
    if (this.props.pauseOnHover) {
      this.setState({ isHovered: false });
    }
  };

  render() {
    const {
      slides = [],
      overlay = true,
      textPosition = "center",
      headline,
      className,
      // eslint-disable-next-line no-unused-vars
      autoPlayInterval, // Used in componentDidMount/componentDidUpdate
      // eslint-disable-next-line no-unused-vars
      pauseOnHover, // Used in componentDidUpdate
      ...props
    } = this.props;

    const { currentSlide, isTransitioning } = this.state;

    // If no slides provided, return null
    if (!slides.length) return null;

    const currentSlideData = slides[currentSlide];

    // Extract className from ctaAttributes so it doesn't override the component's styles when spread
    const { className: ctaClassName, ...ctaProps } =
      currentSlideData?.ctaAttributes || {};

    const heroStyle = currentSlideData?.backgroundImage
      ? { backgroundImage: `url(${currentSlideData.backgroundImage})` }
      : {};

    return (
      <div
        className={classNames(styles.heroSlideshow, className)}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
      >
        {(headline || currentSlideData?.title) && (
          <h1 className={classNames(styles.title, styles.mobileTitle)}>
            {headline || currentSlideData.title}
          </h1>
        )}

        <div
          className={classNames(styles.hero, {
            [styles.overlay]: overlay,
            [styles.textLeft]: textPosition === "left",
            [styles.textRight]: textPosition === "right",
            [styles.textCenter]: textPosition === "center",
            [styles.transitioning]: isTransitioning,
          })}
          style={heroStyle}
          onClick={this.handleHeroClick}
          {...props}
        >
          <div className={styles.content}>
            {(headline || currentSlideData?.title) && (
              <h1 className={classNames(styles.title, styles.desktopTitle)}>
                {headline || currentSlideData.title}
              </h1>
            )}
            {currentSlideData?.ctaText && currentSlideData?.onCtaClick && (
              <BtnUi
                variant={currentSlideData?.ctaVariant || "primary"}
                onClick={currentSlideData.onCtaClick}
                className={classNames(styles.cta, ctaClassName)}
                {...ctaProps}
              >
                {currentSlideData.ctaText}
                <img src={ArrowDownIcon} alt="" />
              </BtnUi>
            )}
          </div>
        </div>
      </div>
    );
  }
}

// Default props for the class component
HeroSlideshow.defaultProps = {
  slides: [],
  autoPlayInterval: 5000,
  pauseOnHover: true,
  overlay: true,
  textPosition: "center",
};

export const Hero = HeroSlideshow;
